import PropTypes from 'prop-types';

import { EVENT_PROPTYPES } from '../../../../constants';

// Keep these in sync with the value in InteractiveMapMarkerTooltipMobile.scss
export const BBOX_SEARCH_BTN_PADDING_FROM_HEADER = 16;
export const ACTION_BAR_OFFSET = 68;

// Max zoom is set to slightly less than the default max,
// which lets you get so close that the map is basically useless.
// Defining it in JS is also useful because we can use it to split up cluster
// names when the user can no longer zoom in any longer
export const MAX_ZOOM = 16;

// When a search is performed, we limit the max zoom so that the
// user has more context of the area around the events. Especially
// useful when only one location is returned.
export const MAX_ZOOM_WHEN_SEARCHING = 14;

export const MOBILE_MAP_FULL_SCREEN = '#mobilemap';

const LOCAL_MAP_STYLES = [
    {
        featureType: 'administrative',
        elementType: 'geometry.fill',
        stylers: [
            {
                color: '#f5f5f5',
            },
        ],
    },
    {
        featureType: 'administrative',
        elementType: 'labels.text',
        stylers: [
            {
                lightness: '15',
            },
        ],
    },
    {
        featureType: 'administrative.locality',
        elementType: 'labels.text.fill',
        stylers: [
            {
                lightness: '-11',
            },
        ],
    },
    {
        featureType: 'administrative.neighborhood',
        elementType: 'labels',
        stylers: [
            {
                visibility: 'on',
            },
        ],
    },
    {
        featureType: 'administrative.land_parcel',
        elementType: 'all',
        stylers: [
            {
                visibility: 'simplified',
            },
        ],
    },
    {
        featureType: 'landscape',
        elementType: 'geometry.fill',
        stylers: [
            {
                color: '#f5f5f5',
            },
            {
                lightness: '-1',
            },
        ],
    },
    {
        featureType: 'landscape.natural.terrain',
        elementType: 'geometry.fill',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'poi.business',
        elementType: 'all',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'road',
        elementType: 'labels',
        stylers: [
            {
                visibility: 'off',
            },
            {
                lightness: 20,
            },
        ],
    },
    {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [
            {
                visibility: 'simplified',
            },
            {
                color: '#ffffff',
            },
            {
                lightness: '15',
            },
        ],
    },
    {
        featureType: 'road.highway',
        elementType: 'labels',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'road.highway.controlled_access',
        elementType: 'geometry',
        stylers: [
            {
                visibility: 'simplified',
            },
            {
                lightness: '0',
            },
            {
                hue: '#ff6500',
            },
        ],
    },
    {
        featureType: 'road.highway.controlled_access',
        elementType: 'labels',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'road.arterial',
        elementType: 'geometry',
        stylers: [
            {
                visibility: 'simplified',
            },
            {
                color: '#ffffff',
            },
        ],
    },
    {
        featureType: 'road.arterial',
        elementType: 'labels',
        stylers: [
            {
                visibility: 'on',
            },
        ],
    },
    {
        featureType: 'road.local',
        elementType: 'geometry',
        stylers: [
            {
                visibility: 'on',
            },
        ],
    },
    {
        featureType: 'road.local',
        elementType: 'geometry.stroke',
        stylers: [
            {
                visibility: 'on',
            },
            {
                color: '#ffffff',
            },
            {
                lightness: '0',
            },
        ],
    },
    {
        featureType: 'road.local',
        elementType: 'labels',
        stylers: [
            {
                visibility: 'off',
            },
            {
                lightness: '28',
            },
        ],
    },
    {
        featureType: 'transit',
        elementType: 'all',
        stylers: [
            {
                visibility: 'off',
            },
            {
                lightness: '15',
            },
        ],
    },
    {
        featureType: 'water',
        elementType: 'all',
        stylers: [
            {
                hue: '#007bff',
            },
            {
                saturation: 30,
            },
            {
                lightness: 49,
            },
        ],
    },
    {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [
            {
                color: '#39c2c9',
            },
            {
                saturation: '-20',
            },
            {
                gamma: '1.00',
            },
            {
                lightness: '46',
            },
        ],
    },
    {
        featureType: 'water',
        elementType: 'labels',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
];

const LOCAL_MAP_OPTIONS = {
    mapTypeId: 'roadmap',
    minZoom: 1,
    disableDefaultUI: true,
    styles: LOCAL_MAP_STYLES,
};

// Override some of the defaults from EDS
export const SEARCH_MAP_OPTIONS = {
    ...LOCAL_MAP_OPTIONS,
    disableDefaultUI: false,
    fullscreenControl: false,
    scrollwheel: false,
    maxZoom: MAX_ZOOM,
    minZoom: 3,
};

// The map bounds automatically change (without user input) one time upon map initialization
export const INITIAL_MAP_BOUNDS_CHANGE_COUNT = 2;

//TEMPORARY: Hard coded far out zoom level
//to gauruntee all events are
//displayed on the map
// TODO: Come up with better way to determine bounds
//on the map
export const DEFAULT_ZOOM_VALUE = 10;

export const GLOBAL_BBOX = [-180, -90, 180, 90];

export const SEARCH_MAP_BASE_PROPTYPES = {
    events: PropTypes.arrayOf(PropTypes.shape(EVENT_PROPTYPES)),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    width: PropTypes.string,
    countryCode: PropTypes.string,
    googleMapLanguage: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    searchByBBox: PropTypes.func,
    setShouldScroll: PropTypes.func,
};

export const SEARCH_MAP_DESKTOP_MOBILE_PROPTYPES = {
    ...SEARCH_MAP_BASE_PROPTYPES,
    onSearchByBounds: PropTypes.func.isRequired,
    onClickOutsideMapMarker: PropTypes.func.isRequired,
    markers: PropTypes.arrayOf(PropTypes.object),
    propsForInteractiveMap: PropTypes.shape({
        height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
        width: PropTypes.string.isRequired,
        language: PropTypes.string,
        region: PropTypes.string,
        center: PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number,
        }).isRequired,
        zoom: PropTypes.number,
        onChange: PropTypes.func,
        onGoogleApiLoaded: PropTypes.func,
    }).isRequired,
};
